import React from 'react';
import { Modal, ModalDialog, DialogTitle, DialogContent, Button, Stack, Typography, Box } from '@mui/joy';
import config from '../../config.ts';

const ApiGuideModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: `90%` }}>
        <DialogTitle>API Usage Guide</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <strong>Base API URL:</strong> <code>{config.apiUrl}</code>
          </Typography>
          
          <Typography sx={{ mb: 2 }}>
            Below are the available API endpoints and detailed instructions on how to use them:
          </Typography>
          
          <Stack spacing={3}>
            {/* Authentication */}
            <Box>
              <Typography sx={{ fontWeight: 'bold' }}>
                1. Login (POST /api/auth/login)
              </Typography>
              <Typography sx={{ mb: 1 }}>
                Authenticate and get access token for your mailbox.
              </Typography>
              <Typography>
                Example Request:
                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
{`curl -X POST "${config.apiUrl}/api/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{
    "username": "your.email@domain.com",
    "password": "your_password"
  }'`}
                </pre>
              </Typography>
              <Typography>
                <strong>Response Body:</strong>
                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
{`{
  "success": true,
  "token": "jwt_token_here",
  "user": {
    "username": "your.email@domain.com",
    "domain": "domain.com"
  }
}`}
                </pre>
              </Typography>
            </Box>

            {/* Change Password */}
            <Box>
              <Typography sx={{ fontWeight: 'bold' }}>
                2. Change Password (POST /api/auth/change-password)
              </Typography>
              <Typography sx={{ mb: 1 }}>
                Change your account password. Requires authentication.
              </Typography>
              <Typography>
                <strong>Required Header:</strong> <code>Authorization: Bearer your_token</code>
              </Typography>
              <br />
              <Typography>
                Example Request:
                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
{`curl -X POST "${config.apiUrl}/api/auth/change-password" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer your_jwt_token" \\
  -d '{
    "oldPassword": "current_password",
    "newPassword": "new_password"
  }'`}
                </pre>
              </Typography>
            </Box>

            {/* Get Inbox */}
            <Box>
              <Typography sx={{ fontWeight: 'bold' }}>
                3. Get Inbox (GET /api/inbox)
              </Typography>
              <Typography sx={{ mb: 1 }}>
                Retrieve emails from your authenticated inbox.
              </Typography>
              <Typography>
                <strong>Required Header:</strong> <code>Authorization: Bearer your_token</code>
              </Typography>
              <br />
              <Typography>
                Example Request:
                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
{`curl -X GET "${config.apiUrl}/api/inbox" \\
  -H "Authorization: Bearer your_jwt_token"`}
                </pre>
              </Typography>
              <Typography>
                <strong>Response Body:</strong>
                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
{`{
  "success": true,
  "mails": [
    {
      "id": "123",
      "messageId": "abc-123",
      "html": "<p>Hello, this is a test mail</p>",
      "headers": {
        "From": "sender@example.com",
        "To": "receiver@example.com"
      },
      "text": "Hello, this is a test mail",
      "subject": "Test mail",
      "sender": {
        "address": "sender@example.com",
        "name": "Sender Name"
      },
      "receiver": {
        "address": "receiver@example.com", 
        "name": "Receiver Name"
      },
      "date": "2025-01-01T00:00:00Z"
    }
  ]
}`}
                </pre>
              </Typography>
            </Box>

            {/* Delete Mails */}
            <Box>
              <Typography sx={{ fontWeight: 'bold' }}>
                4. Delete Mails (DELETE /api/inbox or DELETE /api/inbox/mailId)
              </Typography>
              <Typography sx={{ mb: 1 }}>
                Delete all emails or a specific email by ID.
              </Typography>
              <Typography>
                <strong>Required Header:</strong> <code>Authorization: Bearer your_token</code>
              </Typography>
              <br />
              <Typography>
                Example Request (Delete All):
                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
{`curl -X DELETE "${config.apiUrl}/api/inbox" \\
  -H "Authorization: Bearer your_jwt_token"`}
                </pre>
                Example Request (Delete Specific):
                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
{`curl -X DELETE "${config.apiUrl}/api/inbox/123" \\
  -H "Authorization: Bearer your_jwt_token"`}
                </pre>
              </Typography>
              <Typography>
                <strong>Response Body:</strong>
                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
{`{
  "success": true
}`}
                </pre>
              </Typography>
            </Box>

            {/* Rate Limiting */}
            <Box>
              <Typography sx={{ fontWeight: 'bold' }}>
                Rate Limiting
              </Typography>
              <Typography>
                • Login attempts: 5 attempts per 5 minutes per IP+username combination<br />
                • Password changes: 5 attempts per 5 minutes per IP+username combination
              </Typography>
            </Box>

            {/* Error Responses */}
            <Box>
              <Typography sx={{ fontWeight: 'bold' }}>
                Error Responses
              </Typography>
              <Typography>
                All error responses follow this format:
                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
{`{
  "success": false,
  "error": "Error message description"
}`}
                </pre>
                Common HTTP status codes:<br />
                • 400: Bad Request<br />
                • 401: Unauthorized (invalid/missing token)<br />
                • 403: Forbidden (token expired)<br />
                • 429: Too Many Requests (rate limited)<br />
                • 500: Internal Server Error
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <Button onClick={onClose}>Close</Button>
      </ModalDialog>
    </Modal>
  );
};

export default ApiGuideModal;
